import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Card,
  CardContent,
  Grid,
  useTheme,
  useMediaQuery,
  Stack,
  Chip,
  IconButton,
  Tooltip,
  InputAdornment,
  ToggleButtonGroup,
  ToggleButton,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Divider,
  Avatar
} from '@mui/material';
import {
  Payment as PaymentIcon,
  CalendarToday as CalendarIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Event as EventIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon,
  AttachMoney as AttachMoneyIcon
} from '@mui/icons-material';
import { collection, getDocs, query, orderBy, where, deleteDoc, doc, Timestamp, getDoc, writeBatch, addDoc, updateDoc } from 'firebase/firestore';
import { ref, getDownloadURL, listAll } from 'firebase/storage';
import { db, storage } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { Subscription, PaymentMethod, SubscriptionFormData, SubscriptionType } from '../types/subscription';
import { Event } from '../types/Event';
import { User } from '../types/User';
import { Finance } from '../types/Finance';
import { SubscriptionDialog } from '../components/SubscriptionDialog';
import EventForm from '../components/EventForm';
import PageLayout from '../components/PageLayout';
import PageTitle from '../components/PageTitle';

export const Subscriptions = () => {
  const { user, isAdmin } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [events, setEvents] = useState<{ [key: string]: any }>({});
  const [eventsWithParticipants, setEventsWithParticipants] = useState<any[]>([]);
  const [selectedUser, setSelectedUser] = useState<string>('');
  const [selectedEvent, setSelectedEvent] = useState<string>('');
  const [editMode, setEditMode] = useState<boolean>(false);
  const [subscriptionToEdit, setSubscriptionToEdit] = useState<any>(null);
  const [openEventDialog, setOpenEventDialog] = useState<boolean>(false);
  const [selectedEventForEdit, setSelectedEventForEdit] = useState<Event | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingSubscription, setEditingSubscription] = useState<Subscription | null>(null);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [viewMode, setViewMode] = useState<'users' | 'events'>('users');
  const [eventBackgrounds, setEventBackgrounds] = useState<Record<string, string>>(() => {
    // Récupérer les images depuis le localStorage au chargement initial
    const savedBackgrounds = localStorage.getItem('eventBackgrounds');
    return savedBackgrounds ? JSON.parse(savedBackgrounds) : {};
  });
  const fetchedBackgroundsRef = useRef<Record<string, boolean>>({});
  const currentYear = new Date().getFullYear();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [formData, setFormData] = useState<SubscriptionFormData>({
    amount: '',
    paymentDate: new Date().toISOString().split('T')[0],
    paymentMethod: '',
    type: ''
  });

  useEffect(() => {
    if (openDialog) {
      setSelectedUser('');
      setFormData({
        amount: '',
        paymentDate: new Date().toISOString().split('T')[0],
        paymentMethod: '',
        type: ''
      });
    }
  }, [openDialog]);

  useEffect(() => {
    const loadUsers = async () => {
      const querySnapshot = await getDocs(collection(db, 'users'));
      const usersData = querySnapshot.docs
        .map(doc => ({
          ...doc.data(),
          id: doc.id
        })) as User[];

      setUsers(usersData.filter(user =>
        user.firstName &&
        user.lastName &&
        user.lastName.toUpperCase() !== 'PARTI' &&
        user.firstName !== 'Prénom' &&
        user.lastName !== 'NOM'
      ).sort((a, b) =>
        `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`)
      ));
    };

    loadUsers();
  }, []);

  // Fonction utilitaire pour charger les cotisations
  const loadSubscriptions = async () => {
    const subscriptionsRef = collection(db, 'subscriptions');
    const q = query(subscriptionsRef, orderBy('paymentDate', 'desc'));
    const querySnapshot = await getDocs(q);
    const subscriptionsData = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...(doc.data() as Omit<Subscription, 'id'>)
    }));

    // Récupérer les informations des événements associés aux cotisations
    const eventIds = subscriptionsData
      .filter(sub => sub.type === 'event' && (sub as any).eventId)
      .map(sub => (sub as any).eventId);

    const uniqueEventIds = Array.from(new Set(eventIds));
    const eventsData: { [key: string]: any } = {};

    for (const eventId of uniqueEventIds) {
      try {
        const eventDoc = await getDoc(doc(db, 'events', eventId));
        if (eventDoc.exists()) {
          eventsData[eventId] = {
            ...eventDoc.data(),
            id: eventId
          };
        }
      } catch (error) {
        console.error(`Erreur lors de la récupération de l'événement ${eventId}:`, error);
      }
    }

    setEvents(eventsData);
    setSubscriptions(subscriptionsData);
    setLoading(false);
  };

  // Fonction pour charger tous les événements avec leurs participants
  const loadEventsWithParticipants = async () => {
    try {
      setLoading(true);
      // 1. Récupérer tous les événements
      const eventsRef = collection(db, 'events');
      const eventsSnapshot = await getDocs(eventsRef);
      const eventsData = eventsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Event[];

      // 2. Récupérer toutes les cotisations
      const subscriptionsRef = collection(db, 'subscriptions');
      const subscriptionsSnapshot = await getDocs(subscriptionsRef);
      const subscriptionsData = subscriptionsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Subscription[];

      // 3. Récupérer tous les utilisateurs pour avoir leurs noms
      const usersRef = collection(db, 'users');
      const usersSnapshot = await getDocs(usersRef);
      const usersData = usersSnapshot.docs.reduce((acc, doc) => {
        acc[doc.id] = doc.data();
        return acc;
      }, {} as Record<string, any>);

      // 4. Calculer le montant total payé par chaque adhérent pour chaque année
      // et identifier ceux qui ont payé au moins 10€ (seuil d'adhésion)
      const MEMBERSHIP_THRESHOLD = 10; // Seuil de 10€ pour être considéré comme adhérent
      const yearlyPaymentsByUser: Record<string, Record<number, number>> = {};
      const yearlyMembershipStatus: Record<number, string[]> = {};

      // Initialiser les structures de données
      subscriptionsData.forEach(sub => {
        const userId = sub.userId;

        // Gérer correctement les dates Firestore (Timestamp ou string)
        let paymentDate;
        if (typeof sub.paymentDate === 'string') {
          paymentDate = new Date(sub.paymentDate);
        } else if (sub.paymentDate && (sub.paymentDate as any).seconds) {
          // Timestamp Firestore
          paymentDate = new Date((sub.paymentDate as any).seconds * 1000);
        } else {
          paymentDate = new Date();
        }

        const paymentYear = paymentDate.getFullYear();
        const amount = Number(sub.amount) || 0;

        // Initialiser l'entrée pour cet utilisateur si elle n'existe pas
        if (!yearlyPaymentsByUser[userId]) {
          yearlyPaymentsByUser[userId] = {};
        }

        // Ajouter le montant au total de l'année
        if (!yearlyPaymentsByUser[userId][paymentYear]) {
          yearlyPaymentsByUser[userId][paymentYear] = 0;
        }
        yearlyPaymentsByUser[userId][paymentYear] += amount;

        // Initialiser la liste des membres pour cette année si nécessaire
        if (!yearlyMembershipStatus[paymentYear]) {
          yearlyMembershipStatus[paymentYear] = [];
        }
      });

      // Déterminer les adhérents qui ont payé au moins 10€ pour chaque année
      Object.entries(yearlyPaymentsByUser).forEach(([userId, yearlyAmounts]) => {
        Object.entries(yearlyAmounts).forEach(([year, totalAmount]) => {
          const yearNum = parseInt(year);
          if (totalAmount >= MEMBERSHIP_THRESHOLD) {
            // Créer l'array si nécessaire
            if (!yearlyMembershipStatus[yearNum]) {
              yearlyMembershipStatus[yearNum] = [];
            }
            // Ajouter l'utilisateur s'il n'est pas déjà présent
            if (!yearlyMembershipStatus[yearNum].includes(userId)) {
              yearlyMembershipStatus[yearNum].push(userId);
              console.log(`L'adhérent ${userId} a payé ${totalAmount}€ pour l'année ${yearNum} et est considéré comme membre.`);
            }
          }
        });
      });

      // Afficher les statuts d'adhésion pour le débogage
      console.log('Statuts d\'adhésion par année:', yearlyMembershipStatus);

      // 5. Pour chaque événement, déterminer qui a payé et qui n'a pas payé
      const eventsWithPaymentStatus = eventsData.map(event => {
        // Déterminer l'année de l'événement
        const eventYear = new Date(event.startDate).getFullYear();

        // Récupérer la liste des participants de l'événement
        const allParticipants = event.participants || [];

        // Vérifier si c'est un événement WAN (gratuit)
        const isWanEvent = event.isWanEvent || event.zone === 'wan_online';

        // Pour les événements WAN, tous les participants sont considérés comme ayant payé car c'est gratuit
        if (isWanEvent) {
          console.log(`Événement WAN (gratuit): ${event.title} (${eventYear})`);
          console.log('- Tous les participants sont considérés comme ayant payé car l\'événement est gratuit');

          // Pour les événements WAN, tous les participants sont considérés comme ayant payé
          return {
            ...event,
            participantsDetails: allParticipants.map(participantId => {
              const userData = usersData[participantId] || {};
              return {
                id: participantId,
                firstName: userData.firstName || 'Utilisateur',
                lastName: userData.lastName || 'Inconnu',
                nickname: userData.nickname || '',
                hasPaid: true // Tous ont payé car c'est gratuit
              };
            }),
            paidParticipants: allParticipants,
            unpaidParticipants: [],
            paidCount: allParticipants.length,
            unpaidCount: 0,
            totalParticipants: allParticipants.length,
            isWanEvent: true
          };
        }

        // Pour les événements non-WAN, continuer avec la logique de paiement normale
        // Participants qui ont payé une cotisation annuelle pour cette année
        const annualSubscriptions = subscriptionsData.filter(sub =>
          sub.type === 'annual' &&
          new Date(sub.paymentDate).getFullYear() === eventYear
        );
        const participantsWithAnnualSub = annualSubscriptions.map(sub => sub.userId)
          .filter(userId => allParticipants.includes(userId)); // Filtrer pour ne garder que les participants à cet événement

        // Participants qui ont payé une cotisation spécifique pour cet événement
        const eventSpecificSubscriptions = subscriptionsData.filter(sub =>
          sub.type === 'event' &&
          (sub as any).eventId === event.id
        );
        const participantsWithEventSub = eventSpecificSubscriptions.map(sub => sub.userId);

        // Participants qui ont atteint le seuil de 10€ pour l'année (membres)
        // Filtrer pour ne garder que ceux qui sont participants à cet événement
        const participantsWithMembership = (yearlyMembershipStatus[eventYear] || [])
          .filter(userId => allParticipants.includes(userId));

        // Afficher des informations de débogage pour cet événement
        console.log(`Événement: ${event.title} (${eventYear})`);
        console.log('- Participants avec cotisation annuelle:', participantsWithAnnualSub);
        console.log('- Participants avec cotisation événementielle:', participantsWithEventSub);
        console.log('- Participants ayant payé au moins 10€ dans l\'année et inscrits à cet événement:', participantsWithMembership);

        // IMPORTANT: Un participant est considéré comme ayant payé si:
        // 1. Il a une cotisation annuelle pour l'année de l'événement, OU
        // 2. Il a une cotisation spécifique pour cet événement, OU
        // 3. Il a payé au moins 10€ de cotisations (annuelles ou événementielles) dans l'année de l'événement
        const paidParticipants = Array.from(new Set([...participantsWithAnnualSub, ...participantsWithEventSub, ...participantsWithMembership]));

        // Participants qui n'ont pas payé = tous les participants MOINS ceux qui ont payé
        const unpaidParticipants = allParticipants.filter(p => !paidParticipants.includes(p));

        console.log('- Participants considérés comme ayant payé:', paidParticipants);
        console.log('- Participants n\'ayant pas payé:', unpaidParticipants);

        // Récupérer les détails des utilisateurs pour l'affichage
        const participantsDetails = allParticipants.map(participantId => {
          const userData = usersData[participantId] || {};
          const hasPaid = paidParticipants.includes(participantId);

          return {
            id: participantId,
            firstName: userData.firstName || 'Utilisateur',
            lastName: userData.lastName || 'Inconnu',
            nickname: userData.nickname || '',
            hasPaid
          };
        });

        // Récupérer une image pour l'événement
        if (event.id) {
          // Essayer de trouver une photo prise pendant l'événement
          const eventDate = new Date(event.startDate);
          const eventYear = eventDate.getFullYear();
          const eventMonth = eventDate.getMonth() + 1; // getMonth() retourne 0-11

          // Construire un chemin de stockage basé sur la date de l'événement
          const storagePath = `photos/${eventYear}/${eventMonth.toString().padStart(2, '0')}`;
          fetchImageFromStorage(storagePath, event.id);
        }

        return {
          ...event,
          participantsDetails,
          paidParticipants,
          unpaidParticipants,
          paidCount: participantsDetails.filter(p => p.hasPaid).length,
          unpaidCount: participantsDetails.filter(p => !p.hasPaid).length,
          totalParticipants: participantsDetails.length
        };
      });

      // Trier les événements par date (du plus récent au plus ancien)
      const sortedEvents = eventsWithPaymentStatus.sort((a, b) => {
        return new Date(b.startDate).getTime() - new Date(a.startDate).getTime();
      });

      setEventsWithParticipants(sortedEvents);

      // Mettre à jour le cache des événements pour les cotisations événementielles
      const eventsCache: Record<string, any> = {};
      eventsData.forEach(event => {
        if (event.id) {
          eventsCache[event.id] = event;
        }
      });
      setEvents(eventsCache);

    } catch (error) {
      console.error("Erreur lors du chargement des événements:", error);
      setError("Erreur lors du chargement des événements");
    } finally {
      setLoading(false);
    }
  };

  // Fonction auxiliaire pour récupérer une image depuis Firebase Storage
  const fetchImageFromStorage = async (storagePath: string, eventId: string) => {
    try {
      // Vérifier si l'image est déjà dans notre état local (qui est synchronisé avec localStorage)
      if (eventBackgrounds[eventId] || fetchedBackgroundsRef.current[eventId]) {
        return;
      }
      fetchedBackgroundsRef.current[eventId] = true;

      let currentPath = storagePath;
      let urlFound = '';

      while (currentPath) {
        try {
          const storageRef = ref(storage, currentPath);
          const result = await listAll(storageRef);

          if (result.items.length > 0) {
            const randomIndex = Math.floor(Math.random() * result.items.length);
            const randomImageRef = result.items[randomIndex];
            urlFound = await getDownloadURL(randomImageRef);
            break; // Image trouvée, on sort de la boucle
          }
        } catch (err) {
          // Dossier inexistant ou erreur d'accès, on continue au parent
        }

        const parentPath = currentPath.split('/').slice(0, -1).join('/');
        if (parentPath === currentPath) {
          break; // Impossible de remonter plus haut
        }
        currentPath = parentPath;
      }

      if (urlFound) {
        const img = new Image();
        img.src = urlFound;
        img.onload = () => {
          setEventBackgrounds(prev => {
            const newBackgrounds = { ...prev, [eventId]: urlFound };
            localStorage.setItem('eventBackgrounds', JSON.stringify(newBackgrounds));
            return newBackgrounds;
          });
        };
        img.onerror = () => {
          setEventBackgrounds(prev => {
            const newBackgrounds = { ...prev, [eventId]: 'none' };
            localStorage.setItem('eventBackgrounds', JSON.stringify(newBackgrounds));
            return newBackgrounds;
          });
        };
      } else {
        setEventBackgrounds(prev => {
          const newBackgrounds = { ...prev, [eventId]: 'none' };
          localStorage.setItem('eventBackgrounds', JSON.stringify(newBackgrounds));
          return newBackgrounds;
        });
      }
    } catch (error) {
      console.error(`Erreur lors de la récupération des images depuis Storage: ${storagePath}`, error);
    }
  };

  useEffect(() => {
    loadSubscriptions();
    loadEventsWithParticipants();
    // ...
  }, []);

  // Sauvegarder les images dans le localStorage à chaque mise à jour
  useEffect(() => {
    if (Object.keys(eventBackgrounds).length > 0) {
      localStorage.setItem('eventBackgrounds', JSON.stringify(eventBackgrounds));
    }
  }, [eventBackgrounds]);

  const handleEdit = (subscription: Subscription) => {
    // D'abord fermer le dialogue s'il est ouvert
    setOpenDialog(false);

    // Puis configurer le mode édition et ouvrir le dialogue
    setTimeout(() => {
      setEditMode(true);
      setSubscriptionToEdit(subscription);
      setOpenDialog(true);
    }, 100);
  };

  const handleDelete = async (subscriptionId: string) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette cotisation ?')) {
      try {
        // Supprimer la cotisation
        await deleteDoc(doc(db, 'subscriptions', subscriptionId));

        // Supprimer la transaction associée dans finances
        const financesRef = collection(db, 'finances');
        const q = query(financesRef, where('subscriptionId', '==', subscriptionId));
        const querySnapshot = await getDocs(q);
        const batch = writeBatch(db);
        querySnapshot.forEach((docSnap) => {
          batch.delete(docSnap.ref);
        });
        await batch.commit();

        setSuccess('Cotisation supprimée avec succès');
        loadSubscriptions();
        loadEventsWithParticipants();
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
        setError('Une erreur est survenue lors de la suppression');
      }
    }
  };

  const handleEventCardClick = async (event: Event) => {
    try {
      // Récupérer les données complètes de l'événement
      if (event.id) {
        const eventDoc = await getDoc(doc(db, 'events', event.id));
        if (eventDoc.exists()) {
          const eventData = { id: eventDoc.id, ...eventDoc.data() } as Event;
          setSelectedEventForEdit(eventData);
          setOpenEventDialog(true);
        }
      }
    } catch (error) {
      console.error('Error fetching event details:', error);
    }
  };

  // Fonction pour annuler l'inscription d'un participant à un événement
  const handleCancelParticipation = async (eventId: string, participantId: string) => {
    try {
      if (!eventId || !participantId) return;

      // Référence au document de l'événement
      const eventRef = doc(db, 'events', eventId);

      // Récupérer l'événement actuel
      const eventDoc = await getDoc(eventRef);
      if (!eventDoc.exists()) {
        setError("L'événement n'existe pas.");
        return;
      }

      const eventData = eventDoc.data();
      const participants = eventData.participants || [];

      // Vérifier si l'utilisateur est inscrit
      if (!participants.includes(participantId)) {
        setError("Ce participant n'est pas inscrit à cet événement.");
        return;
      }

      // Supprimer le participant de la liste
      const updatedParticipants = participants.filter((id: string) => id !== participantId);

      // Mettre à jour le document de l'événement
      await updateDoc(eventRef, {
        participants: updatedParticipants
      });

      // Afficher un message de succès
      setSuccess(`L'inscription de ${getUserName(participantId)} a été annulée avec succès.`);

      // Recharger les événements pour mettre à jour l'interface
      loadEventsWithParticipants();
    } catch (error) {
      console.error("Erreur lors de l'annulation de l'inscription:", error);
      setError("Une erreur est survenue lors de l'annulation de l'inscription.");
    }
  };

  const handleSubmit = async () => {
    try {
      if (!selectedUser || !formData.amount || !formData.paymentMethod || !formData.type) {
        setError('Veuillez remplir tous les champs obligatoires');
        return;
      }

      // Crée la cotisation
      const subscriptionRef = collection(db, 'subscriptions');
      const subscriptionData: Omit<Subscription, 'id'> = {
        userId: selectedUser,
        amount: Number(formData.amount),
        paymentDate: formData.paymentDate,
        paymentMethod: formData.paymentMethod,
        type: formData.type,
        year: new Date(formData.paymentDate).getFullYear(),
        createdAt: new Date().toISOString(),
        createdBy: user?.uid || ''
      };
      const subscriptionDoc = await addDoc(subscriptionRef, subscriptionData);

      // Crée la transaction finances avec subscriptionId (FORCÉ)
      const financeRef = collection(db, 'finances');
      const selectedUserData = users.find(u => u.id === selectedUser);
      const financeData: any = {
        type: 'income',
        category: 'subscription',
        amount: Number(formData.amount),
        description: `Cotisation ${subscriptionData.year} - ${(selectedUserData?.firstName ?? '')} ${(selectedUserData?.lastName ?? '')}`,
        date: formData.paymentDate,
        userId: selectedUser,
        subscriptionId: subscriptionDoc.id, // <-- FORCÉ
        createdAt: new Date().toISOString(),
        createdBy: user?.uid || ''
      };
      console.log('financeData envoyé à Firestore :', financeData);
      if (!financeData.subscriptionId) {
        setError('Impossible de créer la transaction financière : subscriptionId manquant.');
        return;
      }
      await addDoc(financeRef, financeData);

      setSuccess('Cotisation ajoutée avec succès');
      setOpenDialog(false);
      setSelectedUser('');
      setFormData({
        amount: '',
        paymentDate: new Date().toISOString().split('T')[0],
        paymentMethod: '',
        type: ''
      });

      // Recharger les cotisations
      const newQuerySnapshot = await getDocs(collection(db, 'subscriptions'));
      const newSubscriptions = newQuerySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Subscription[];
      setSubscriptions(newSubscriptions);
    } catch (error) {
      setError('Erreur lors de la création de la cotisation');
      console.error('Erreur handleSubmit:', error);
      setError('Erreur lors de l\'ajout de la cotisation');
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingSubscription(null);
    setSelectedUser('');
    setFormData({
      amount: '',
      paymentDate: new Date().toISOString().split('T')[0],
      paymentMethod: '',
      type: ''
    });
  };

  const getUserName = (userId: string) => {
    const user = users.find(u => u.id === userId);
    if (!user) return 'Utilisateur inconnu';
    return `${user.firstName} ${user.lastName}${user.nickname ? ` (${user.nickname})` : ''}`;
  };

  // Fonction pour obtenir le nom de l'événement
  const getEventInfo = (eventId: string) => {
    const event = events[eventId];
    if (!event) return 'Événement inconnu';

    const date = event.startDate ? new Date(event.startDate).toLocaleDateString('fr-FR') : '';
    return `${event.title} (${date})`;
  };

  // Fonction pour formater la méthode de paiement
  const formatPaymentMethod = (method: string) => {
    switch (method) {
      case 'especes': return 'Espèces';
      case 'cheque': return 'Chèque';
      case 'virement': return 'Virement internet';
      default: return method;
    }
  };

  const MobileView = () => (
    <Grid container spacing={2}>
      {subscriptions.map((subscription) => (
        <Grid item xs={12} key={subscription.userId + subscription.year}>
          <Card sx={{ bgcolor: '#333', color: '#fff' }}>
            <CardContent>
              <Stack spacing={2}>
                {/* En-tête avec nom et année */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6">
                    {getUserName(subscription.userId)}
                  </Typography>
                  <Chip
                    label={subscription.year}
                    color="primary"
                    size="small"
                  />
                </Box>

                {/* Montant et mode de paiement */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PaymentIcon fontSize="small" />
                  <Typography>
                    {subscription.amount}€ ({formatPaymentMethod(subscription.paymentMethod || '')})
                  </Typography>
                </Box>

                {/* Type de cotisation */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    Type: {subscription.type === 'annual' ? 'Annuelle' :
                      subscription.type === 'event' && (subscription as any).eventId ?
                        `Événementielle - ${getEventInfo((subscription as any).eventId)}` :
                        subscription.type}
                  </Typography>
                </Box>

                {/* Date de paiement */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CalendarIcon fontSize="small" />
                  <Typography>
                    {subscription.paymentDate && typeof subscription.paymentDate === 'object' && (subscription.paymentDate as any).seconds ?
                      new Date((subscription.paymentDate as any).seconds * 1000).toLocaleDateString('fr-FR') :
                      subscription.paymentDate && !isNaN(Date.parse(subscription.paymentDate.toString())) ?
                        new Date(subscription.paymentDate.toString()).toLocaleDateString('fr-FR') :
                        '—'}
                  </Typography>
                </Box>

                {/* Boutons d'édition et de suppression */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Tooltip title="Modifier">
                    <IconButton
                      onClick={() => handleEdit(subscription)}
                      color="primary"
                      size="small"
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Supprimer">
                    <IconButton
                      onClick={() => handleDelete(subscription.id!)}
                      color="error"
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  const DesktopView = () => (
    <TableContainer component={Paper} sx={{ bgcolor: '#333' }}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={{ color: '#fff' }}>Adhérent</TableCell>
            <TableCell sx={{ color: '#fff' }}>Type</TableCell>
            <TableCell sx={{ color: '#fff' }}>Année</TableCell>
            <TableCell sx={{ color: '#fff' }}>Montant</TableCell>
            <TableCell sx={{ color: '#fff' }}>Date de paiement</TableCell>
            <TableCell sx={{ color: '#fff' }}>Mode de paiement</TableCell>
            <TableCell sx={{ color: '#fff' }} align="center">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {subscriptions.map((subscription) => (
            <TableRow key={subscription.userId + subscription.year + (subscription.id || '')}>
              <TableCell sx={{ color: '#fff' }}>{getUserName(subscription.userId)}</TableCell>
              <TableCell sx={{ color: '#fff' }}>
                {subscription.type === 'annual' ? 'Annuelle' :
                  subscription.type === 'event' && (subscription as any).eventId ?
                    <Tooltip title={getEventInfo((subscription as any).eventId)}>
                      <span>Événementielle</span>
                    </Tooltip> :
                    subscription.type}
              </TableCell>
              <TableCell sx={{ color: '#fff' }}>{subscription.year}</TableCell>
              <TableCell sx={{ color: '#fff' }}>{subscription.amount}€</TableCell>
              <TableCell sx={{ color: '#fff' }}>
                {subscription.paymentDate && typeof subscription.paymentDate === 'object' && (subscription.paymentDate as any).seconds ?
                  new Date((subscription.paymentDate as any).seconds * 1000).toLocaleDateString('fr-FR') :
                  subscription.paymentDate && !isNaN(Date.parse(subscription.paymentDate.toString())) ?
                    new Date(subscription.paymentDate.toString()).toLocaleDateString('fr-FR') :
                    '—'}
              </TableCell>
              <TableCell sx={{ color: '#fff' }}>
                {formatPaymentMethod(subscription.paymentMethod || '')}
              </TableCell>
              <TableCell sx={{ color: '#fff' }} align="center">
                <Tooltip title="Modifier">
                  <IconButton
                    onClick={() => handleEdit(subscription)}
                    color="primary"
                    size="small"
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Supprimer">
                  <IconButton
                    onClick={() => handleDelete(subscription.id!)}
                    color="error"
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  if (!isAdmin) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Typography>Vous n'avez pas accès à cette page.</Typography>
      </Container>
    );
  }

  // Fonction pour calculer le pourcentage de participants ayant payé
  const calculatePaymentPercentage = (event: any) => {
    if (!event.participants || event.participants.length === 0) return 0;
    return Math.round((event.paidCount / event.participants.length) * 100);
  };

  // Vue des événements avec le statut de paiement des participants
  const EventsView = () => (
    <>
      {eventsWithParticipants.length === 0 ? (
        <Typography sx={{ color: '#fff', mt: 2 }}>Aucun événement trouvé</Typography>
      ) : (
        <Grid container spacing={3}>
          {eventsWithParticipants.map((event) => {
            const eventId = event.id;
            const hasBackground = eventId && eventBackgrounds[eventId] && eventBackgrounds[eventId] !== 'none';
            return (
              <Grid item xs={12} key={event.id}>
                <Card
                  sx={{
                    mb: 2,
                    bgcolor: 'rgba(23, 42, 69, 0.7)', // Fond bleu semi-transparent pour les cartes sans photos
                    color: 'white',
                    borderRadius: 2,
                    cursor: 'pointer',
                    border: '1px solid rgba(255, 255, 255, 0.12)',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    position: 'relative',
                    ...(hasBackground ? {
                      backgroundImage: `url("${eventBackgrounds[eventId]}")`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      backgroundRepeat: 'no-repeat',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(10, 25, 47, 0.2)', // Overlay léger
                        zIndex: 0
                      }
                    } : {}),
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      transition: 'all 0.2s ease-in-out',
                      ...(hasBackground ? {
                        '&::before': {
                          backgroundColor: 'rgba(10, 25, 47, 0.1)' // Plus transparent au survol
                        }
                      } : {
                        backgroundColor: 'rgba(23, 42, 69, 0.8)' // Plus foncé au survol pour les cartes sans photos
                      })
                    }
                  }}
                  onClick={() => handleEventCardClick(event)}
                >
                  <CardContent sx={{ position: 'relative', zIndex: 1 }}>
                    <Box sx={{ mb: 2 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          ...(hasBackground ? {
                            textShadow: '0 0 10px rgba(0,0,0,0.8), 0 0 5px rgba(0,0,0,0.9)',
                            fontWeight: 'bold',
                            backgroundColor: 'rgba(0,0,0,0.6)',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            display: 'inline-block',
                            width: 'fit-content'
                          } : {})
                        }}
                      >
                        {event.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: hasBackground ? '#fff' : '#aaa',
                          ...(hasBackground ? {
                            textShadow: '0 0 8px rgba(0,0,0,0.9), 0 0 4px rgba(0,0,0,0.9)',
                            fontWeight: 'bold',
                            backgroundColor: 'rgba(0,0,0,0.5)',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            display: 'inline-block',
                            mt: 1
                          } : {})
                        }}
                      >
                        {new Date(event.startDate).toLocaleDateString('fr-FR')}
                      </Typography>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                      {event.isWanEvent ? (
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          Participants: {event.participants?.length || 0} <span style={{ fontStyle: 'italic', color: '#4caf50' }}>(Evénement gratuit)</span>
                        </Typography>
                      ) : (
                        <>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            Participants ayant payé: {event.paidCount} / {event.participants?.length || 0}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ flexGrow: 1 }}>
                              <LinearProgress
                                variant="determinate"
                                value={calculatePaymentPercentage(event)}
                                sx={{
                                  height: 10,
                                  borderRadius: 5,
                                  backgroundColor: 'rgba(255,255,255,0.1)',
                                  '& .MuiLinearProgress-bar': {
                                    backgroundColor: calculatePaymentPercentage(event) < 50 ? '#ff9800' : '#4caf50'
                                  }
                                }}
                              />
                            </Box>
                            <Typography variant="body2">
                              {calculatePaymentPercentage(event)}%
                            </Typography>
                          </Box>
                        </>
                      )}

                      {/* Liste des participants */}
                      {event.paidParticipants && event.paidParticipants.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <CheckCircleIcon fontSize="small" sx={{ color: '#4caf50' }} />
                            {event.isWanEvent ? 'Participants:' : 'Participants ayant payé:'}
                          </Typography>
                          <List dense sx={{ bgcolor: 'rgba(0,0,0,0.2)', borderRadius: 1, maxHeight: 150, overflow: 'auto' }}>
                            {event.paidParticipants.map((participantId: string) => (
                              <ListItem key={participantId}>
                                <ListItemText
                                  primary={getUserName(participantId)}
                                  sx={{ '& .MuiListItemText-primary': { color: '#4caf50' } }}
                                />
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                  {!event.isWanEvent && (
                                    <Chip
                                      size="small"
                                      label="Payé"
                                      color="success"
                                      sx={{ height: 24 }}
                                    />
                                  )}
                                  <Tooltip title="Annuler l'inscription">
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={(e) => {
                                        e.stopPropagation(); // Empêcher l'ouverture du dialogue d'édition d'événement
                                        if (window.confirm(`Êtes-vous sûr de vouloir annuler l'inscription de ${getUserName(participantId)} ?`)) {
                                          handleCancelParticipation(event.id || '', participantId);
                                        }
                                      }}
                                    >
                                      <CancelIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </ListItem>
                            ))}
                          </List>
                        </Box>
                      )}
                    </Box>

                    {/* Afficher la section "Participants n'ayant pas payé" uniquement pour les événements non-WAN */}
                    {!event.isWanEvent && event.unpaidParticipants && event.unpaidParticipants.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <WarningIcon fontSize="small" sx={{ color: '#ff9800' }} />
                          Participants n'ayant pas payé:
                        </Typography>
                        <List dense sx={{ bgcolor: 'rgba(0,0,0,0.2)', borderRadius: 1, maxHeight: 150, overflow: 'auto' }}>
                          {event.unpaidParticipants.map((participantId: string) => (
                            <ListItem key={participantId}>
                              <ListItemText
                                primary={getUserName(participantId)}
                                sx={{ '& .MuiListItemText-primary': { color: '#ff9800' } }}
                              />
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="warning"
                                  onClick={(e) => {
                                    e.stopPropagation(); // Empêcher l'ouverture du dialogue d'édition d'événement
                                    // D'abord réinitialiser
                                    setOpenDialog(false);
                                    // Puis définir les valeurs et ouvrir
                                    setTimeout(() => {
                                      setSelectedUser(participantId);
                                      setSelectedEvent(event.id || '');
                                      setEditMode(false);
                                      setSubscriptionToEdit(null);
                                      setOpenDialog(true);
                                    }, 100);
                                  }}
                                >
                                  Enregistrer paiement
                                </Button>
                                <Tooltip title="Annuler l'inscription">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={(e) => {
                                      e.stopPropagation(); // Empêcher l'ouverture du dialogue d'édition d'événement
                                      if (window.confirm(`Êtes-vous sûr de vouloir annuler l'inscription de ${getUserName(participantId)} ?`)) {
                                        handleCancelParticipation(event.id || '', participantId);
                                      }
                                    }}
                                  >
                                    <CancelIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
        </Grid>
      )}
    </>
  );

  return (
    <PageLayout>
      <PageTitle
        title="Gestion des Cotisations"
        icon={<AttachMoneyIcon />}
        action={
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              setSelectedUser('');
              setSelectedEvent('');
              setEditMode(false);
              setSubscriptionToEdit(null);
              setOpenDialog(true);
            }}
          >
            Nouvelle cotisation
          </Button>
        }
      />
      <SubscriptionDialog
        open={openDialog}
        onClose={() => {
          setOpenDialog(false);
          setSelectedUser('');
          setSelectedEvent('');
          setEditMode(false);
          setSubscriptionToEdit(null);
          loadSubscriptions();
          loadEventsWithParticipants();
        }}
        users={users}
        selectedUserId={selectedUser}
        selectedEventId={selectedEvent}
        editMode={editMode}
        subscriptionToEdit={subscriptionToEdit}
      />

      {/* Dialog pour éditer un événement */}
      <Dialog
        open={openEventDialog}
        onClose={() => setOpenEventDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Modifier l'événement
        </DialogTitle>
        <DialogContent>
          <EventForm
            event={selectedEventForEdit}
            onClose={() => setOpenEventDialog(false)}
            onSave={() => {
              setOpenEventDialog(false);
              loadEventsWithParticipants();
            }}
          />
        </DialogContent>
      </Dialog>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mt: 2 }}>{success}</Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(e, newMode) => newMode && setViewMode(newMode)}
          aria-label="Mode d'affichage"
          sx={{
            bgcolor: '#333',
            '& .MuiToggleButton-root': {
              color: '#fff',
              '&.Mui-selected': {
                bgcolor: 'primary.main',
                color: '#fff',
                '&:hover': {
                  bgcolor: 'primary.dark',
                }
              }
            }
          }}
        >
          <ToggleButton value="users" aria-label="Vue par adhérent">
            <PersonIcon sx={{ mr: 1 }} /> Adhérents
          </ToggleButton>
          <ToggleButton value="events" aria-label="Vue par événement">
            <EventIcon sx={{ mr: 1 }} /> Événements
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {viewMode === 'users' ? (
        isMobile ? <MobileView /> : <DesktopView />
      ) : (
        <EventsView />
      )}
    </PageLayout>
  );
}
